import React, { useCallback, useEffect, useRef } from "react";
import { animated, config, useTransition } from "@react-spring/web";
import { Scroll } from "@tvg/design-system/web";
import { Container, List } from "./styled-components";

export interface DropdownProps {
  id: string;
  selectedValue?: string;
  isVisible: boolean;
  onClickAway: () => void;
  children: React.ReactNode | React.ReactNode[];
  width?: number;
  position?: "left" | "right";
}

const AnimatedContainer = animated(Container);

export const Dropdown = ({
  id,
  selectedValue,
  isVisible,
  onClickAway,
  children,
  width = 365,
  position = "left"
}: DropdownProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const webScrollRef = useRef<HTMLDivElement>(null);

  const transition = useTransition(isVisible, {
    config: isVisible ? { ...config.stiff } : { duration: 150 },
    from: { opacity: 0, transform: `translate3d(0px, 20px, 0px)` },
    enter: { opacity: 1, transform: `translate3d(0px, 0px, 0px)` },
    leave: { opacity: 0, transform: `translate3d(0px, 20px, 0px)` }
  });

  useEffect(() => {
    const container = webScrollRef?.current!;
    const element = document.getElementById(`dropdown-button-${selectedValue}`);

    if (container && element) {
      const containerDOMRect = container.getBoundingClientRect();
      const elementDOMRect = element.getBoundingClientRect();
      const visibleArea = containerDOMRect.height - elementDOMRect.height;
      const desiredScrollPosition =
        elementDOMRect.top - containerDOMRect.top - visibleArea;

      if (desiredScrollPosition > 0) {
        container.scrollTo({
          top: desiredScrollPosition,
          behavior: "smooth"
        });
      }
    }
  }, [isVisible]);

  const handleClickAway = useCallback(
    (event) => {
      const isSelectorButton = event.target.id && event.target.id === id;

      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        !isSelectorButton
      ) {
        onClickAway();
      }
    },
    [ref, onClickAway, id]
  );

  useEffect(() => {
    document.addEventListener("click", handleClickAway, true);
    return () => {
      document.removeEventListener("click", handleClickAway, true);
    };
  }, [ref, onClickAway]);

  return (
    <>
      {transition(
        (styles, isOpen) =>
          isOpen && (
            <AnimatedContainer
              data-qa-label="selector-dropdown"
              ref={ref}
              style={styles}
              width={width}
              position={position}
            >
              <Scroll
                height="auto"
                maxHeight="min(540px, calc(100vh - 140px))"
                minHeight="140px"
                overflowY="auto"
                overflowX="hidden"
                showBorderLeft
                hasShadow
              >
                <List>{children}</List>
              </Scroll>
            </AnimatedContainer>
          )
      )}
    </>
  );
};

export default Dropdown;
